import { useId } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router';
import { isAnalyticsConfigured } from '@/analytics/analytics';
import { setConsent } from '@/analytics/consentStore';
import { useConsent } from '@/analytics/useConsent';

/**
 * Asks once whether analytics may use cookies.
 *
 * Accept and decline are the same size, the same shape and one click each. That is not
 * decoration: consent is only meaningful if refusing is as easy as agreeing, and a
 * banner where "no" is a faint link is the pattern regulators name specifically.
 *
 * Not a modal. It does not trap focus, does not cover the page and does not block
 * reading - a visitor who wants to ignore it can. Focus is deliberately left where it
 * is; stealing it on load would interrupt a screen reader mid-sentence to ask about
 * cookies.
 */
export function ConsentBanner() {
  const decision = useConsent();
  const headingId = useId();
  const descriptionId = useId();

  // Nothing to consent to when analytics is not configured, which is every local and
  // CI build. Rendering it there would train everyone to dismiss it without reading.
  if (!isAnalyticsConfigured() || decision !== null) {
    return null;
  }

  return (
    <Box
      component="section"
      role="region"
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
      sx={{
        position: 'fixed',
        insetInline: 0,
        bottom: 0,
        zIndex: (t) => t.zIndex.appBar + 2,
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        py: 3,
      }}
    >
      <Container>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2.5, md: 4 }}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
        >
          <Stack spacing={1} sx={{ maxWidth: '62ch' }}>
            <Typography id={headingId} variant="h4" component="h2">
              Cookies
            </Typography>
            <Typography id={descriptionId} variant="body2" color="text.secondary">
              We use Google Analytics to see which pages get read. Until you agree it runs without
              cookies and cannot recognise you from one visit to the next.{' '}
              <Link component={RouterLink} to="/privacy">
                What we collect
              </Link>
              .
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => {
                setConsent('denied');
              }}
            >
              Decline
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => {
                setConsent('granted');
              }}
            >
              Accept
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
